import React, { useEffect, useState } from "react";
import { Card, CardContent, Typography, Box, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchAllWorkcells, fetchWorkcellStatsGrid } from "../api";
import getIconSrc from "../utils/getIconSrc";
import { formatHoursMinutes } from "../utils/time";
import { useCustomerCurrency, useCustomerLocale } from "../context/CustomerSettingsContext";
import { formatLocalDate } from "../utils/localDateTime";

const WorkcellStatsGrid = ({ date_param }) => {
  const [workcells, setWorkcells] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { formatCurrency } = useCustomerCurrency();
  const locale = useCustomerLocale();
  const range_days = 28;
  const date = date_param || formatLocalDate(new Date());

  const formatPercent = (value, fractionDigits) => {
    const num = Number(value);
    const safe = Number.isFinite(num) ? num : 0;
    return new Intl.NumberFormat(locale, {
      style: "percent",
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(safe / 100);
  };

  // Use centralized helper to determine icon source
  // (moved to frontend/src/utils/getIconSrc.js)
  // getIconSrc(wc) -> string|null

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const allWorkcells = await fetchAllWorkcells();
        // Only include workcells with has_oee: true and sort by SortCode
        let filteredWorkcells = allWorkcells.filter(wc => wc.has_oee)
          .sort((a, b) => (a.SortCode ?? 0) - (b.SortCode ?? 0));
        setWorkcells(filteredWorkcells);

        // Calculate rolling 30-day window
        const endDate = date;
        // const startDate = format(subDays(new Date(date), 29), "yyyy-MM-dd");

        // Fetch consolidated stats from the new endpoint and map into the
        // existing stats object shape expected by the UI.
        let statsArr = [];
        try {
          // fetchWorkcellStatsGrid(endDate, range_days) — returns an array of stats per workcell
          statsArr = await fetchWorkcellStatsGrid(endDate, range_days);
        } catch (e) {
          console.error('Error fetching workcell stats grid:', e);
          statsArr = [];
        }

        const statsObj = {};
        // Normalize whatever the API returns into the shape used below
        for (const s of statsArr) {
          const id = s.workcell_id ?? s.workcellId ?? s.id;
          if (!id) continue;
          // Prefer the API's `operations` field (from get_workcell_stats_grid)
          const _opsNum = Number(s.operations ?? 0);
          // Prefer `turnover` from the API
          const _turnNum = Number(s.turnover ?? 0);
          // Prefer `worked_hours` from the API
          const _workedNum = Number(s.worked_hours ?? 0);
          statsObj[id] = {
            activityAverage: Number(s.activityAverage ?? s.activity_average ?? s.activity_pct ?? s.activity ?? 0),
            oee: Number(s.oee ?? s.oee_pct ?? s.oee_percentage ?? 0),
            totalOps: Number.isFinite(_opsNum) ? _opsNum : 0,
            totalTurnover: Number.isFinite(_turnNum) ? _turnNum : 0,
            hoursWorked: Number.isFinite(_workedNum) ? _workedNum : 0,
            SortCode: Number(s.SortCode ?? s.sort_code ?? 0)
          };
        }

        // Ensure every workcell has an entry (fallback defaults)
        for (const wc of filteredWorkcells) {
          if (!statsObj[wc.workcell_id]) {
            statsObj[wc.workcell_id] = {
              activityAverage: 0,
              oee: 0,
              totalOps: 0,
              totalTurnover: 0,
              hoursWorked: 0,
              SortCode: wc.SortCode || 0
            };
          }
        }

        setStats(statsObj);
      } catch (e) {
        console.error('Error in fetchStats:', e);
        setStats({});
      }
      setLoading(false);
    };
    fetchStats();
    // Only re-run if date changes
  }, [date]);

  if (loading) return <Box display="flex" justifyContent="center" my={4}><CircularProgress /></Box>;

  return (
    <Box sx={{ mt: 3 }} style={{ position: "relative" }}>
      <Typography variant="h4" gutterBottom>
        {t("workcells.stats.latestDays", { days: range_days })}
      </Typography>
      <Box ml={2} style={{ position: 'absolute', right: 0, top: 0, marginTop: "0" }}>
        <a href="/workcell-factory-view" style={{ textDecoration: 'none' }}>
          <button style={{
            background: '#1976d2',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            padding: '6px 16px',
            fontSize: '1rem',
            cursor: 'pointer',
            fontWeight: 500
          }}>
            {t("workcells.stats.factoryFloorView")}
          </button>
        </a>
      </Box>
      <Box
        sx={{
          mt: 1,
          display: 'grid',
          // fixed-width columns of 300px, wrap as needed
          gridTemplateColumns: 'repeat(auto-fill, 300px)',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        {workcells.map(wc => (
          <div key={wc.workcell_id}>
            <Card
              sx={{ cursor: 'pointer', height: '100%', display: 'flex', flexDirection: 'column' }}
              onClick={() => navigate(`/chart/workcell/${wc.workcell_id}/monthly/${date}`)}
            >
              <CardContent sx={{ flex: 1 }}>
                <Box mb={1} display="flex" alignItems="center">
                  {(() => {
                    const src = getIconSrc(wc);
                    return src ? (
                      <img
                        src={src}
                        alt={t("workcells.stats.iconAlt", { name: wc.workcell_name })}
                        onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/placeholder-icon.svg'; }}
                        style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4, marginRight: 12 }}
                      />
                    ) : (
                      <img src="/placeholder-icon.svg" alt={t("common.placeholder")} style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4, marginRight: 12 }} />
                    );
                  })()}
                </Box>
                <Typography variant="h6">{wc.workcell_name}</Typography>
                <Typography>{t("workcells.stats.activity")}: {formatPercent(stats[wc.workcell_id]?.activityAverage, 2)}</Typography>
                <Typography>{t("workcells.stats.oee")}: {formatPercent(stats[wc.workcell_id]?.oee, 1)}</Typography>
                <Typography>{t("workcells.stats.operations")}: {stats[wc.workcell_id]?.totalOps}</Typography>
                <Typography>{t("workcells.stats.worked")}: {formatHoursMinutes(stats[wc.workcell_id]?.hoursWorked)}</Typography>
                <Typography>{t("workcells.stats.turnover")}: {formatCurrency(stats[wc.workcell_id]?.totalTurnover)}</Typography>
              </CardContent>
            </Card>
          </div>
        ))}
      </Box>
    </Box>
  );
};

export default WorkcellStatsGrid;
